import React, { useState } from 'react';
import styled from 'styled-components';
import {
  UploadSectionStyle,
  FileUploadForm,
  FileInput,
  Button,
  Label,
  PageTitle,
  UploadStatus,
} from '../../../styles/HomeStyles';
import { apiService } from '../../../services/api';

const SandboxOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0;
  padding: 0.875rem 1rem;
  border: 1.5px solid ${({ checked }) => checked ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  background: ${({ checked }) => checked ? '#eff6ff' : '#f9fafb'};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

const SandboxCheckbox = styled.input`
  margin-top: 2px;
  accent-color: #3b82f6;
  width: 15px;
  height: 15px;
  cursor: pointer;
  flex-shrink: 0;
`;

const SandboxLabel = styled.div`
  flex: 1;

  span {
    display: block;
    font-size: 0.875rem;
    font-weight: 600;
    color: #1f2937;
  }

  p {
    margin: 0.2rem 0 0 0;
    font-size: 0.78rem;
    color: #6b7280;
    line-height: 1.4;
  }
`;

const MAX_FILE_SIZE_MB = process.env.REACT_APP_MAX_FILE_SIZE_MB || 40;

const FileUpload = ({ onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [sendToSandbox, setSendToSandbox] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    // Controllo dimensione file
    if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setUploadStatus({
        success: false,
        message: `The file is too large to be analyzed. Maximum size is ${MAX_FILE_SIZE_MB}MB.`
      });
      return;
    }

    setFile(selectedFile);
    setUploadStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate file selection
    if (!file) {
      setUploadStatus({
        success: false,
        message: 'Please select a file to upload'
      });
      return;
    }

    // Validate file type (APK)
    /*
    if (!file.name.toLowerCase().endsWith('.apk')) {
      setUploadStatus({
        success: false,
        message: 'Please select an APK file'
      });
      return;
    }
    */

    // Prepare form data
    const formData = new FormData();
    formData.append('file', file);
    formData.append('send_to_sandbox', sendToSandbox ? 'true' : 'false');

    try {
      // Use apiService to upload file
      const response = await fetch(`${apiService.baseUrl}/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        // Successful upload
        setUploadStatus({
          success: true,
          message: data.message || 'File uploaded successfully',
          fileId: data.file_id,
          isNew: data.is_new_upload
        });

        // Reset form
        setFile(null);
        e.target.reset();

        // Trigger parent component's refresh
        onUploadSuccess && onUploadSuccess();
      } else {
        // Upload failed
        setUploadStatus({
          success: false,
          message: data.detail || 'Upload failed'
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus({
        success: false,
        message: 'Upload failed due to network error'
      });
    }
  };

  return (
    <UploadSectionStyle>
      <PageTitle>Upload APK for Analysis</PageTitle>
      <FileUploadForm onSubmit={handleSubmit}>
        <div>
          <Label>Select APK File</Label>
          <FileInput
            type="file"
            accept=".apk"
            onChange={handleFileChange}
          />
        </div>
        <SandboxOption checked={sendToSandbox}>
          <SandboxCheckbox
            type="checkbox"
            checked={sendToSandbox}
            onChange={e => setSendToSandbox(e.target.checked)}
          />
          <SandboxLabel>
            <span>Also submit to dynamic sandbox</span>
            <p>Automatically run behavioral analysis after static analysis completes.</p>
          </SandboxLabel>
        </SandboxOption>
        <Button
          primary
          type="submit"
          disabled={!file}
        >
          Upload and Analyze
        </Button>
        
        {uploadStatus && (
          <UploadStatus 
            success={uploadStatus.success} 
            warning={!uploadStatus.isNew}
          >
            <p>{uploadStatus.message}</p>
            {uploadStatus.fileId && (
              <p>File ID: {uploadStatus.fileId}</p>
            )}
          </UploadStatus>
        )}
      </FileUploadForm>
    </UploadSectionStyle>
  );
};

export default FileUpload;